import React, { useMemo, useCallback } from 'react';
import useLanguage from '@op/hooks/useLanguage';
import {
  IVersionTitleItem,
  IVersionCascadeOption,
} from '@op/pages/SteamPerformance/hooks/useVersionTitleList';
import type { IVersionConfig } from '@op/pages/SteamPerformance/VersionAnalysis/config';
import MultiSelectFilter from '@op/components/SelectComponents/MultiSelectFilter';
import CascadeSelectFilter from '@op/components/SelectComponents/CascadeSelectFilter';
import './index.scss';

/** 版本筛选状态（存储唯一值，用于 UI 受控模式） */
export interface IVersionFilterState {
  /** 选中的版本标题列表（可能含唯一后缀） */
  update_titles?: string[];
  /** 选中的 Chapter 列表（仅配置游戏） */
  chapters?: string[];
  /** 选中的 Release 列表（仅配置游戏） */
  releases?: string[];
  /** 选中的 Patch 列表（可能含唯一后缀） */
  patches?: string[];
  /** version_filters 模式下的结构化筛选数据（如 [{ season: '3', chapter: '4' }]） */
  version_filters?: Array<Record<string, string>>;
}

/** 去除唯一后缀（__xxx），还原为 API 可用的原始值 */
export const stripUniqueKey = (val: string): string => {
  const idx = val.indexOf('__');
  return idx > -1 ? val.substring(0, idx) : val;
};

/** 清理 IVersionFilterState 中的唯一后缀，用于构建 API 参数 */
export const cleanVersionFilter = (state: IVersionFilterState): IVersionFilterState => {
  const result: IVersionFilterState = {
    ...state,
    update_titles: state.update_titles?.map(stripUniqueKey),
  };
  return result;
};

export interface VersionCascadeFilterProps {
  /** 版本标题列表 */
  titleList: IVersionTitleItem[];
  /** 配置游戏级联选项 */
  cascadeOptions: IVersionCascadeOption[] | null;
  /** 是否为配置游戏 */
  isConfigGame: boolean;
  /** 版本配置（用于动态生成 levelTitles 及 version_filters 判断） */
  versionConfig: IVersionConfig | null;
  /** 当前版本筛选值 */
  value: IVersionFilterState;
  /** 筛选条件变更回调 */
  onChange: (value: IVersionFilterState) => void;
}

const VersionCascadeFilter: React.FC<VersionCascadeFilterProps> = ({
  titleList,
  cascadeOptions,
  isConfigGame,
  versionConfig,
  value,
  onChange,
}) => {
  const { formatMessage, isEn } = useLanguage();

  // 普通 Update Title 选项（title + start_time + end_time 作为唯一 key）
  const titleOptions = useMemo(() => {
    return (titleList || []).map((item: IVersionTitleItem) => ({
      value: `${item.update_title_en}__${item.start_time}__${item.end_time}`,
      text: isEn ? item.update_title_en : item.update_title_cn,
    }));
  }, [titleList, isEn]);

  // 转换 IVersionCascadeOption 到 CascadeOption (label -> text)
  const transformedCascadeOptions = useMemo(() => {
    if (!cascadeOptions) return [];

    const transformNode = (node: IVersionCascadeOption): any => ({
      value: node.value,
      text: node.label,
      children: node.children?.map(transformNode),
    });

    return cascadeOptions.map(transformNode);
  }, [cascadeOptions]);

  // 动态生成 levelTitles
  const levelTitles = useMemo(() => {
    if (!versionConfig?.levels) return [];
    return versionConfig.levels.map(level => formatMessage({ id: level.label }));
  }, [versionConfig, formatMessage]);

  // 根据 releases 反查 chapters
  const deriveChapters = useCallback(
    (releases: string[]) => {
      const selectedChapters = new Set<string>();

      const traverseToFindParent = (nodes: IVersionCascadeOption[], targetValue: string) => {
        for (const node of nodes) {
          if (node.children) {
            for (const child of node.children) {
              if (child.value === targetValue) {
                selectedChapters.add(node.value);
                return true;
              }
            }
          }
        }
        return false;
      };

      releases.forEach(releaseValue => {
        if (cascadeOptions) {
          traverseToFindParent(cascadeOptions, releaseValue);
        }
      });

      return Array.from(selectedChapters);
    },
    [cascadeOptions],
  );

  // 级联选择关闭时：提交变更
  const handleCascadeClose = useCallback(
    (_transformed: any, rawLeafValues?: string[]) => {
      const releases = rawLeafValues ?? [];
      const chapters = deriveChapters(releases);

      const newFilter: IVersionFilterState = {
        ...value,
        chapters,
        releases,
      };

      // version_filters = true：将叶子值（如 "3__4"）拆解为结构化对象 [{ season: '3', chapter: '4' }]
      if (versionConfig?.version_filters) {
        const levelKeys = versionConfig.levels.map(l => l.value);
        newFilter.version_filters = releases.map(releaseValue => {
          const parts = releaseValue.split('__');
          const obj: Record<string, string> = {};
          levelKeys.forEach((key, index) => {
            obj[key] = parts[index] ?? '';
          });
          return obj;
        });

        delete newFilter.chapters;
        delete newFilter.releases;
      }

      onChange(newFilter);
    },
    [onChange, value, deriveChapters, versionConfig],
  );

  // Update Title 关闭时提交变更（保留唯一值用于 UI 受控）
  const handleTitleClose = useCallback(
    (update_titles: string[]) => {
      onChange({
        ...value,
        update_titles,
      });
    },
    [onChange, value],
  );


  return (
    <div className="dbo-version-cascade-filter">
      {/* 普通游戏：Update Title 多选下拉（Conan 等配置游戏可隐藏） */}
      {!versionConfig?.hideTitleFilter && (
        <div className="dbo-version-cascade-filter__title">
          <MultiSelectFilter
            options={titleOptions}
            matchButtonWidth={false}
            emptyAsAll
            value={value.update_titles}
            onClose={handleTitleClose}
            placeholder={formatMessage({ id: 'Update Title' })}
          />
        </div>
      )}

      {/* 配置游戏：多级级联筛选 */}
      {isConfigGame && (
        <div className="dbo-version-cascade-filter__cascade">
          <CascadeSelectFilter
            options={transformedCascadeOptions}
            value={value.releases}
            emptyAsAll
            returnMode="smart"
            levelTitles={levelTitles}
            onClose={handleCascadeClose}
            placeholder={formatMessage({ id: 'Version' })}
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(VersionCascadeFilter);
